import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Table, Input, Label, ButtonGroup, Col } from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";

// (2026-08) Reporte de Cotizaciones: conversión de cotizaciones a pedido,
// con resumen por empresa emisora, rendimiento por vendedor y el detalle.
function ReporteCotizaciones() {
  const { user } = useContext(AuthContext);
  const URL_COTIZACIONES = process.env.REACT_APP_URL_COTIZACIONES;
  const URL_COLABORADORES = process.env.REACT_APP_URL_COLABORADORES;
  const URL_EMPRESAS = process.env.REACT_APP_URL_EMPRESAS;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 90);

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(
    moment(inicio).format("YYYY-MM-DD")
  );
  const [selectedFechaFin, setSelectedFechaFin] = useState(
    moment(hoy).format("YYYY-MM-DD")
  );

  const [cotizaciones, setCotizaciones] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [clientes, setClientes] = useState([]);

  const [selectedEmpresa, setSelectedEmpresa] = useState("");
  const [selectedVendedor, setSelectedVendedor] = useState("");
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedEstatus, setSelectedEstatus] = useState("");
  const [cargando, setCargando] = useState(false);

  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };

  const arr = (d) => (Array.isArray(d) ? d : []);
  const money = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  const pct = (parte, total) =>
    Number(total || 0) > 0 ? (Number(parte || 0) / Number(total)) * 100 : 0;
  const fechaCorta = (f) => (f ? moment(f).format("DD/MM/YYYY") : "-");

  useEffect(() => {
    axios
      .get(URL_COLABORADORES, { headers })
      .then((r) => setColaboradores(arr(r.data)))
      .catch((e) => console.log(e));
    axios
      .get(URL_EMPRESAS, { headers })
      .then((r) => setEmpresas(arr(r.data)))
      .catch((e) => console.log(e));
    axios
      .get(URL_CLIENTES, { headers })
      .then((r) => setClientes(arr(r.data)))
      .catch((e) => console.log(e));
  }, []);

  const cargaCotizaciones = () => {
    setCargando(true);
    axios
      .get(`${URL_COTIZACIONES}Fechas/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers,
      })
      .then((r) => {
        const data = arr(r.data)
          .filter((c) => c.is_active !== "No")
          .map((c) => {
            const pedido = arr(c.pedidos)[0] || null;
            const colab = arr(c.colaboradores)[0] || {};
            return {
              _id: c._id,
              folio: c.idCotizacion,
              fecha: c.fecha,
              empresa: arr(c.empresas)[0]?.name || "SIN EMPRESA",
              idEmpresa: arr(c.empresas)[0]?._id || "",
              vendedor: `${colab.nombre || ""} ${colab.apellido || ""}`.trim() || "SIN VENDEDOR",
              idVendedor: colab._id || "",
              cliente:
                arr(c.clientes)[0]?.nombre_comercial ||
                // (2026-10) cotización a un prospecto del CRM
                (arr(c.crmProspectos)[0]
                  ? `${arr(c.crmProspectos)[0].empresa || arr(c.crmProspectos)[0].razon_social} (Prospecto)`
                  : "SIN CLIENTE"),
              idCliente: arr(c.clientes)[0]?._id || "",
              subtotal: Number(c.subTotal || c.total_general || 0),
              convertida: !!pedido,
              montoPedido: pedido ? Number(pedido.total_general || 0) : 0,
              fechaPedido: pedido ? pedido.fecha : "",
              idPedido: pedido ? pedido.idPedido : "",
            };
          })
          .sort((a, b) => Number(b.folio) - Number(a.folio));
        setCotizaciones(data);
        setCargando(false);
      })
      .catch((e) => {
        console.log(e);
        setCargando(false);
        Swal.fire("Error", "No se pudieron cargar las cotizaciones", "error");
      });
  };

  useEffect(() => {
    cargaCotizaciones();
  }, [selectedFechaInicio, selectedFechaFin]);

  // ---------------- Filtros ----------------
  const filtradas = useMemo(
    () =>
      cotizaciones.filter(
        (c) =>
          (!selectedEmpresa || c.idEmpresa === selectedEmpresa) &&
          (!selectedVendedor || c.idVendedor === selectedVendedor) &&
          (!selectedCliente || c.idCliente === selectedCliente) &&
          (!selectedEstatus ||
            (selectedEstatus === "Convertidos" ? c.convertida : !c.convertida))
      ),
    [
      cotizaciones,
      selectedEmpresa,
      selectedVendedor,
      selectedCliente,
      selectedEstatus,
    ]
  );

  // ---------------- Totales ----------------
  const totalCotizado = filtradas.reduce((a, c) => a + c.subtotal, 0);
  const totalConvertido = filtradas.reduce((a, c) => a + c.montoPedido, 0);
  const numConvertidas = filtradas.filter((c) => c.convertida).length;

  const porEmpresa = useMemo(() => {
    const m = {};
    filtradas.forEach((c) => {
      if (!m[c.empresa]) {
        m[c.empresa] = { empresa: c.empresa, cotizaciones: 0, convertidas: 0, cotizado: 0, convertido: 0 };
      }
      m[c.empresa].cotizaciones += 1;
      m[c.empresa].cotizado += c.subtotal;
      if (c.convertida) {
        m[c.empresa].convertidas += 1;
        m[c.empresa].convertido += c.montoPedido;
      }
    });
    return Object.values(m).sort((a, b) => b.cotizado - a.cotizado);
  }, [filtradas]);

  const porVendedor = useMemo(() => {
    const m = {};
    filtradas.forEach((c) => {
      if (!m[c.vendedor]) {
        m[c.vendedor] = {
          vendedor: c.vendedor,
          empresas: {},
          cotizaciones: 0,
          cotizado: 0,
          convertido: 0,
        };
      }
      m[c.vendedor].cotizaciones += 1;
      m[c.vendedor].cotizado += c.subtotal;
      m[c.vendedor].convertido += c.montoPedido;
      m[c.vendedor].empresas[c.empresa] = (m[c.vendedor].empresas[c.empresa] || 0) + 1;
    });
    return Object.values(m)
      .map((v) => ({
        ...v,
        // empresa donde más cotiza (como en el ejemplo: "Empresa Principal")
        empresaPrincipal:
          Object.entries(v.empresas).sort((a, b) => b[1] - a[1])[0]?.[0] || "",
      }))
      .sort((a, b) => b.cotizado - a.cotizado);
  }, [filtradas]);

  // ---------------- PDF ----------------
  const PDFReporte = () => {
    if (!filtradas.length) {
      return Swal.fire("Sin datos", "No hay cotizaciones en el periodo/filtros", "warning");
    }

    const doc = new jsPDF("landscape", "mm", "a4");
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const ML = 10;
    const usableW = pageW - ML * 2;

    const azulOscuro = [31, 56, 100];
    const azul = [46, 117, 182];
    const azulClaro = [232, 240, 250];
    const verde = [39, 124, 70];
    const verdeClaro = [235, 245, 237];
    const gris = [110, 120, 135];
    const bordeGris = [210, 216, 224];

    // ---- Banda de título
    doc.setFillColor(...azulOscuro);
    doc.rect(0, 0, pageW, 18, "F");
    doc.setTextColor(255).setFont(undefined, "bold").setFontSize(15);
    doc.text("LANGIANI ERP - REPORTE DE COTIZACIONES", ML, 9);
    doc.setFont(undefined, "normal").setFontSize(9);
    doc.text(
      "Análisis de Cotización por Empresa Emisora, Vendedor y Conversión a Pedido",
      ML,
      14.5
    );
    doc.setTextColor(0);

    // ---- Datos del filtro
    const nombreEmpresa =
      empresas.find((e) => e._id === selectedEmpresa)?.name || "TODAS";
    const colabSel = colaboradores.find((c) => c._id === selectedVendedor);
    const nombreVendedor = colabSel
      ? `${colabSel.nombre || ""} ${colabSel.apellido || ""}`.trim()
      : "TODOS";
    const nombreCliente =
      clientes.find((c) => c._id === selectedCliente)?.nombre_comercial ||
      "TODOS LOS CLIENTES";

    let y = 25;
    doc.setFontSize(8.5);
    const linea = (etiqueta, valor, yy, x, alineado) => {
      doc.setFont(undefined, "bold").setTextColor(...azulOscuro);
      const w = doc.getTextWidth(`${etiqueta} `);
      if (alineado === "right") {
        doc.setFont(undefined, "normal").setTextColor(60);
        const wv = doc.getTextWidth(valor);
        doc.setFont(undefined, "bold").setTextColor(...azulOscuro);
        doc.text(etiqueta, x - wv - 1, yy, { align: "right" });
        doc.setFont(undefined, "normal").setTextColor(60);
        doc.text(valor, x, yy, { align: "right" });
      } else {
        doc.text(etiqueta, x, yy);
        doc.setFont(undefined, "normal").setTextColor(60);
        doc.text(valor, x + w, yy);
      }
      doc.setTextColor(0);
    };

    linea("Periodo:", `${fechaCorta(selectedFechaInicio)} - ${fechaCorta(selectedFechaFin)}`, y, ML);
    linea("Empresa Emisora:", nombreEmpresa, y + 4.5, ML);
    linea(
      "Estatus Pedido:",
      selectedEstatus || "TODOS (Convertidos y Pendientes)",
      y + 9,
      ML
    );
    linea("Fecha Impresión:", moment(hoy).format("DD/MM/YYYY"), y, pageW - ML, "right");
    linea("Vendedores:", nombreVendedor, y + 4.5, pageW - ML, "right");
    linea("Cliente:", nombreCliente, y + 9, pageW - ML, "right");

    y += 14;

    // ---- Tarjetas de indicadores
    const tarjetas = [
      {
        titulo: "MONTO SUBTOTAL COTIZADO",
        valor: money(totalCotizado),
        pie: `${filtradas.length} Cotizaciones Emitidas`,
        color: azul,
        fondo: azulClaro,
      },
      {
        titulo: "CONVERTIDO A PEDIDO",
        valor: money(totalConvertido),
        pie: `${numConvertidas} Pedidos Confirmados`,
        color: verde,
        fondo: verdeClaro,
      },
      {
        titulo: "TASA DE CONVERSIÓN (%)",
        valor: `${pct(totalConvertido, totalCotizado).toFixed(2)}%`,
        pie: "Monto Ganado vs Cotizado",
        color: azul,
        fondo: azulClaro,
      },
    ];

    const gap = 4;
    const tarjW = (usableW - gap * (tarjetas.length - 1)) / tarjetas.length;
    const tarjH = 20;
    tarjetas.forEach((t, i) => {
      const x = ML + i * (tarjW + gap);
      doc.setFillColor(...t.fondo).setDrawColor(...bordeGris).setLineWidth(0.2);
      doc.rect(x, y, tarjW, tarjH, "FD");
      doc.setFillColor(...t.color);
      doc.rect(x, y, tarjW, 1.2, "F");
      doc.setFont(undefined, "bold").setFontSize(8).setTextColor(...gris);
      doc.text(t.titulo, x + tarjW / 2, y + 6, { align: "center" });
      doc.setFontSize(14).setTextColor(...t.color);
      doc.text(t.valor, x + tarjW / 2, y + 13, { align: "center" });
      doc.setFont(undefined, "normal").setFontSize(7.5).setTextColor(...gris);
      doc.text(t.pie, x + tarjW / 2, y + 17.5, { align: "center" });
      doc.setTextColor(0);
    });

    y += tarjH + 7;

    const seccion = (titulo, yyOriginal) => {
      let yy = yyOriginal;
      // que el título no quede huérfano al final de la hoja
      if (yy + 26 > pageH - 14) {
        doc.addPage();
        yy = 18;
      }
      doc.setFillColor(...azul);
      doc.rect(ML, yy - 3.6, 1.6, 5, "F");
      doc.setFont(undefined, "bold").setFontSize(10).setTextColor(...azulOscuro);
      doc.text(titulo, ML + 4, yy);
      doc.setTextColor(0).setFont(undefined, "normal");
      return yy + 2;
    };

    const tablaBase = {
      margin: { left: ML, right: ML, top: 12, bottom: 12 },
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.6,
        lineColor: bordeGris,
        lineWidth: 0.15,
        textColor: 40,
      },
      headStyles: {
        fillColor: azul,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    };

    // ---- 1. Resumen por empresa emisora
    y = seccion("1. RESUMEN COMPARATIVO POR EMPRESA EMISORA", y);
    doc.autoTable({
      ...tablaBase,
      startY: y,
      head: [[
        "EMPRESA EMISORA", "SUBTOTAL COTIZACIONES", "COTIZACIONES CONVERTIDAS EN PEDIDO",
        "SUBTOTAL COTIZADO ($)", "MONTO CONVERTIDO ($)", "% CONVERSIÓN",
      ]],
      columnStyles: {
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      body: porEmpresa.map((e) => [
        e.empresa,
        String(e.cotizaciones),
        String(e.convertidas),
        money(e.cotizado),
        money(e.convertido),
        `${pct(e.convertido, e.cotizado).toFixed(2)}%`,
      ]),
      foot: [[
        "SUBTOTAL GENERAL",
        String(filtradas.length),
        String(numConvertidas),
        money(totalCotizado),
        money(totalConvertido),
        `${pct(totalConvertido, totalCotizado).toFixed(2)}%`,
      ]],
      footStyles: {
        fillColor: azulClaro,
        textColor: azulOscuro,
        fontStyle: "bold",
        halign: "right",
      },
    });

    y = doc.autoTable.previous.finalY + 8;

    // ---- 2. Rendimiento por vendedor
    y = seccion("2. RENDIMIENTO COMERCIAL POR VENDEDOR Y EMPRESA", y);
    doc.autoTable({
      ...tablaBase,
      startY: y,
      head: [[
        "VENDEDOR", "EMPRESA PRINCIPAL", "# DE COTIZACIONES",
        "SUBTOTAL COTIZADO ($)", "MONTO CONVERTIDO ($)", "% CIERRE",
      ]],
      columnStyles: {
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      body: porVendedor.map((v) => [
        v.vendedor,
        v.empresaPrincipal,
        String(v.cotizaciones),
        money(v.cotizado),
        money(v.convertido),
        `${pct(v.convertido, v.cotizado).toFixed(2)}%`,
      ]),
    });

    y = doc.autoTable.previous.finalY + 8;

    // ---- 3. Detalle
    y = seccion("3. DETALLE TRANSACCIONAL DE COTIZACIONES", y);
    doc.autoTable({
      ...tablaBase,
      startY: y,
      head: [[
        "EMPRESA", "# COTIZACION", "FECHA", "VENDEDOR", "CLIENTE/EMPRESA",
        "SUBTOTAL COTIZADO", "MONTO PEDIDO ($)", "FECHA DE PEDIDO",
      ]],
      columnStyles: {
        1: { halign: "center" },
        2: { halign: "center" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "center" },
      },
      body: filtradas.map((c) => [
        c.empresa,
        String(c.folio),
        fechaCorta(c.fecha),
        c.vendedor,
        c.cliente,
        money(c.subtotal),
        c.convertida ? money(c.montoPedido) : "-",
        c.convertida ? fechaCorta(c.fechaPedido) : "-",
      ]),
    });

    // ---- Pie
    const paginas = doc.internal.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      doc.setPage(p);
      doc.setDrawColor(...bordeGris).setLineWidth(0.2);
      doc.line(ML, pageH - 10, pageW - ML, pageH - 10);
      doc.setFont(undefined, "normal").setFontSize(8).setTextColor(...gris);
      doc.text("LANGIANI ERP - Reporte de Cotizaciones y Conversión", ML, pageH - 5.5);
      doc.text(`Página ${p} de ${paginas}`, pageW - ML, pageH - 5.5, { align: "right" });
      doc.setTextColor(0);
    }

    window.open(doc.output("bloburl"), "_blank");
  };

  // ---------------- Excel ----------------
  const excel = () => {
    const option = {};
    option.fileName = "ReporteCotizaciones";
    option.datas = [
      {
        sheetData: filtradas.map((c) => ({
          Empresa: c.empresa,
          Cotizacion: c.folio,
          Fecha: c.fecha,
          Vendedor: c.vendedor,
          Cliente: c.cliente,
          Subtotal: c.subtotal,
          Convertida: c.convertida ? "Si" : "No",
          MontoPedido: c.montoPedido,
          FechaPedido: c.fechaPedido || "",
        })),
        sheetName: "Cotizaciones",
        sheetFilter: ["Empresa","Cotizacion","Fecha","Vendedor","Cliente","Subtotal","Convertida","MontoPedido","FechaPedido"],
        sheetHeader: ["Empresa","Cotizacion","Fecha","Vendedor","Cliente","Subtotal","Convertida","Monto Pedido","Fecha Pedido"],
      },
    ];
    new ExportJsonExcel(option).saveExcel();
  };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_ventas ? (
        <div className="card container col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuVentas" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFReporte}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Reporte de Cotizaciones</h3>

          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => setSelectedFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => setSelectedFechaFin(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Empresa</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedEmpresa}
                onChange={(e) => setSelectedEmpresa(e.target.value)}
              >
                <option value="">Todas</option>
                {empresas
                  .sort((a, b) => (a.name > b.name ? 1 : -1))
                  .map((e) => (
                    <option key={e._id} value={e._id}>
                      {e.name}
                    </option>
                  ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Vendedor</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedVendedor}
                onChange={(e) => setSelectedVendedor(e.target.value)}
              >
                <option value="">Todos</option>
                {colaboradores
                  .sort((a, b) => (a.nombre > b.nombre ? 1 : -1))
                  .map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.nombre} {c.apellido}
                    </option>
                  ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Cliente</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedCliente}
                onChange={(e) => setSelectedCliente(e.target.value)}
              >
                <option value="">Todos</option>
                {clientes
                  .sort((a, b) =>
                    a.nombre_comercial > b.nombre_comercial ? 1 : -1
                  )
                  .map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.nombre_comercial}
                    </option>
                  ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label>Estatus Pedido</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedEstatus}
                onChange={(e) => setSelectedEstatus(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="Convertidos">Convertidos</option>
                <option value="Pendientes">Pendientes</option>
              </Input>
            </Col>
          </Row>

          <br />

          {/* Indicadores */}
          <Row>
            <Col md={4}>
              <div className="card p-2 text-center" style={{ borderTop: "3px solid #2E75B6" }}>
                <small className="text-muted">MONTO SUBTOTAL COTIZADO</small>
                <h4 style={{ color: "#2E75B6" }}>{money(totalCotizado)}</h4>
                <small className="text-muted">{filtradas.length} Cotizaciones Emitidas</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="card p-2 text-center" style={{ borderTop: "3px solid #277C46" }}>
                <small className="text-muted">CONVERTIDO A PEDIDO</small>
                <h4 style={{ color: "#277C46" }}>{money(totalConvertido)}</h4>
                <small className="text-muted">{numConvertidas} Pedidos Confirmados</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="card p-2 text-center" style={{ borderTop: "3px solid #2E75B6" }}>
                <small className="text-muted">TASA DE CONVERSIÓN</small>
                <h4 style={{ color: "#2E75B6" }}>
                  {pct(totalConvertido, totalCotizado).toFixed(2)}%
                </h4>
                <small className="text-muted">Monto Ganado vs Cotizado</small>
              </div>
            </Col>
          </Row>

          <br />
          {cargando ? <h5 align="center">Cargando...</h5> : null}

          <h5 className="text-primary">1. Resumen comparativo por empresa emisora</h5>
          <Table size="sm" striped borderless className="table-responsive-xl">
            <thead>
              <tr>
                <th>Empresa Emisora</th>
                <th className="text-center">Cotizaciones</th>
                <th className="text-center">Convertidas</th>
                <th className="text-end">Subtotal Cotizado</th>
                <th className="text-end">Monto Convertido</th>
                <th className="text-end">% Conversión</th>
              </tr>
            </thead>
            <tbody>
              {porEmpresa.map((e) => (
                <tr key={e.empresa}>
                  <td>{e.empresa}</td>
                  <td className="text-center">{e.cotizaciones}</td>
                  <td className="text-center">{e.convertidas}</td>
                  <td className="text-end">{money(e.cotizado)}</td>
                  <td className="text-end">{money(e.convertido)}</td>
                  <td className="text-end">{pct(e.convertido, e.cotizado).toFixed(2)}%</td>
                </tr>
              ))}
              <tr className="negrita">
                <td className="negrita">SUBTOTAL GENERAL</td>
                <td className="text-center negrita">{filtradas.length}</td>
                <td className="text-center negrita">{numConvertidas}</td>
                <td className="text-end negrita">{money(totalCotizado)}</td>
                <td className="text-end negrita">{money(totalConvertido)}</td>
                <td className="text-end negrita">
                  {pct(totalConvertido, totalCotizado).toFixed(2)}%
                </td>
              </tr>
            </tbody>
          </Table>

          <h5 className="text-primary">2. Rendimiento comercial por vendedor</h5>
          <Table size="sm" striped borderless className="table-responsive-xl">
            <thead>
              <tr>
                <th>Vendedor</th>
                <th className="text-center">Empresa Principal</th>
                <th className="text-center"># Cotizaciones</th>
                <th className="text-end">Subtotal Cotizado</th>
                <th className="text-end">Monto Convertido</th>
                <th className="text-end">% Cierre</th>
              </tr>
            </thead>
            <tbody>
              {porVendedor.map((v) => (
                <tr key={v.vendedor}>
                  <td>{v.vendedor}</td>
                  <td className="text-center">{v.empresaPrincipal}</td>
                  <td className="text-center">{v.cotizaciones}</td>
                  <td className="text-end">{money(v.cotizado)}</td>
                  <td className="text-end">{money(v.convertido)}</td>
                  <td className="text-end">{pct(v.convertido, v.cotizado).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <h5 className="text-primary">3. Detalle transaccional de cotizaciones</h5>
          <Table size="sm" striped borderless className="table-responsive-xl">
            <thead>
              <tr>
                <th>Empresa</th>
                <th className="text-center"># Cotización</th>
                <th className="text-center">Fecha</th>
                <th>Vendedor</th>
                <th>Cliente</th>
                <th className="text-end">Subtotal Cotizado</th>
                <th className="text-end">Monto Pedido</th>
                <th className="text-center">Fecha de Pedido</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.length ? (
                filtradas.map((c) => (
                  <tr key={c._id}>
                    <td>{c.empresa}</td>
                    <td className="text-center">{c.folio}</td>
                    <td className="text-center">{fechaCorta(c.fecha)}</td>
                    <td>{c.vendedor}</td>
                    <td>{c.cliente}</td>
                    <td className="text-end">{money(c.subtotal)}</td>
                    <td className="text-end">
                      {c.convertida ? money(c.montoPedido) : "-"}
                    </td>
                    <td className="text-center">
                      {c.convertida ? fechaCorta(c.fechaPedido) : "-"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center text-muted">
                    No hay cotizaciones en el periodo seleccionado
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <br />
        </div>
      ) : undefined}
      <br />
    </>
  );
}

export default ReporteCotizaciones;
